export type StorageBand = {
  fromDay: number;
  toDay: number | null;
  rate20: number;
  rate40_45: number;
};

export type StorageService = {
  key: string;
  name: string;
  enabled: boolean;
  sortOrder: number;
  vatPercent: number;
  bands: StorageBand[];
};

export type StorageTariff = { currency: 'USD'; services: StorageService[] };
export type StorageInput = {
  serviceKey: string;
  containerSize: '20' | '40' | '45';
  startDate: string;
  endDate: string;
  quantity: number;
};
export type StorageResult = {
  service: StorageService;
  band: StorageBand;
  days: number;
  quantity: number;
  containerSize: StorageInput['containerSize'];
  unitPrice: number;
  netTotal: number;
  vatAmount: number;
  grossTotal: number;
};

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Tarife verisi geçersiz.');
  return value as Record<string, unknown>;
}

function integer(value: unknown, min: number, max: number, label: string): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
    throw new Error(`${label}, ${min}–${max} aralığında tam sayı olmalıdır.`);
  }
  return value;
}

function decimal(value: unknown, max: number, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > max ||
    Math.abs(value * 100 - Math.round(value * 100)) > 0.000001) {
    throw new Error(`${label}, 0–${max} aralığında ve en fazla iki ondalıklı olmalıdır.`);
  }
  return value;
}

export function validateStorageTariff(value: unknown): StorageTariff {
  const tariff = object(value);
  if (tariff.currency !== 'USD') throw new Error('Tarife para birimi USD olmalıdır.');
  if (!Array.isArray(tariff.services) || tariff.services.length < 1 || tariff.services.length > 50) {
    throw new Error('Tarifede 1–50 hizmet bulunmalıdır.');
  }
  const keys = new Set<string>();
  const services = tariff.services.map((entry): StorageService => {
    const service = object(entry);
    if (typeof service.key !== 'string' || service.key !== service.key.trim() || service.key.length > 64 ||
      !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(service.key) || keys.has(service.key)) {
      throw new Error('Hizmet anahtarları benzersiz olmalı; küçük harf, rakam ve tire içermelidir.');
    }
    keys.add(service.key);
    if (typeof service.name !== 'string' || !service.name.trim() || service.name.trim().length > 160 || /[\u0000-\u001f\u007f-\u009f]/.test(service.name)) {
      throw new Error('Hizmet adı gerekli ve en fazla 160 karakter olmalıdır.');
    }
    if (typeof service.enabled !== 'boolean') throw new Error('Hizmetin yayın durumu geçersiz.');
    if (!Array.isArray(service.bands) || service.bands.length < 1 || service.bands.length > 20) {
      throw new Error('Her hizmette 1–20 gün dilimi bulunmalıdır.');
    }
    let expectedDay = 1;
    const bands = service.bands.map((item, index): StorageBand => {
      const band = object(item);
      const fromDay = integer(band.fromDay, 1, 36500, 'Başlangıç günü');
      const toDay = band.toDay === null ? null : integer(band.toDay, 1, 36500, 'Bitiş günü');
      if (fromDay !== expectedDay || (toDay !== null && toDay < fromDay) ||
        (index === (service.bands as unknown[]).length - 1 ? toDay !== null : toDay === null)) {
        throw new Error('Gün dilimleri 1’den başlayıp boşluk veya çakışma olmadan ilerlemeli; son dilim süresiz olmalıdır.');
      }
      expectedDay = toDay === null ? fromDay : toDay + 1;
      return { fromDay, toDay, rate20: decimal(band.rate20, 1000000, '20′ fiyatı'),
        rate40_45: decimal(band.rate40_45, 1000000, '40′/45′ fiyatı') };
    });
    return { key: service.key, name: service.name.trim(), enabled: service.enabled,
      sortOrder: integer(service.sortOrder, 0, 1000000, 'Hizmet sırası'),
      vatPercent: decimal(service.vatPercent, 100, 'KDV oranı'), bands };
  });
  return { currency: 'USD', services };
}

function dateValue(value: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('Giriş ve çıkış tarihlerini seçin.');
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(milliseconds) || new Date(milliseconds).toISOString().slice(0, 10) !== value) {
    throw new Error('Giriş ve çıkış tarihleri geçerli olmalıdır.');
  }
  return milliseconds;
}

/** The stay length selects ONE band; its price is charged once per container. */
export function calculateStorageFee(value: StorageTariff, input: StorageInput): StorageResult {
  const tariff = validateStorageTariff(value);
  const service = tariff.services.find(item => item.key === input.serviceKey && item.enabled);
  if (!service) throw new Error('Yayındaki bir hizmet seçin.');
  if (!['20', '40', '45'].includes(input.containerSize)) throw new Error('Konteyner tipi geçersiz.');
  if (!Number.isSafeInteger(input.quantity) || input.quantity < 1) throw new Error('Konteyner adedi pozitif tam sayı olmalıdır.');
  const start = dateValue(input.startDate);
  const end = dateValue(input.endDate);
  if (end < start) throw new Error('Çıkış tarihi giriş tarihinden önce olamaz.');
  const days = Math.round((end - start) / 86400000) + 1;
  const band = service.bands.find(item => days >= item.fromDay && (item.toDay === null || days <= item.toDay));
  if (!band) throw new Error('Bu gün sayısı için tarife bulunamadı.');
  const unitPrice = input.containerSize === '20' ? band.rate20 : band.rate40_45;
  const netCents = Math.round(unitPrice * 100) * input.quantity;
  const vatNumerator = netCents * Math.round(service.vatPercent * 100);
  if (!Number.isSafeInteger(netCents) || !Number.isSafeInteger(vatNumerator)) throw new Error('Hesaplanan tutar çok büyük.');
  const vatCents = Math.round(vatNumerator / 10000);
  if (!Number.isSafeInteger(netCents + vatCents)) throw new Error('Hesaplanan tutar çok büyük.');
  return { service, band, days, quantity: input.quantity, containerSize: input.containerSize,
    unitPrice, netTotal: netCents / 100, vatAmount: vatCents / 100, grossTotal: (netCents + vatCents) / 100 };
}
